/*
 * The Marketing suite's request shapes and answers (draft 12): Google profile and reviews, rank grids, website rank, the
 * page optimizer, ad spend and conversions, channels, social posts and the media library, cold email and the lead finder.
 */
import { z } from "zod";
import {
  GRID_SIZES,
  OUTREACH_MAX_CAP,
  OUTREACH_MAX_STEPS,
  WEB_RANK_MAX,
  type Channel,
  type PageBrief,
} from "./marketing-suite.js";

const text = (max: number) => z.string().trim().max(max);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// Google profile -------------------------------------------------------------------------------------------------------

export const gbpProfileBody = z.object({ description: text(1000), services: z.array(text(80).min(1)).max(40) }).strict();
/** Picks which Google listing is the company's own (the first name match can be another business). */
export const gbpPickBody = z.object({ placeId: text(300).min(1) }).strict();
export const gbpChoicesQuery = z.object({ q: text(120).optional() });
export const reviewReplyBody = z.object({ reply: text(1000).min(1, "Write a reply."), approve: z.boolean() }).strict();
export const gbpPostBody = z
  .object({
    service: text(80).min(1, "Name the service."),
    area: text(80).min(1, "Name the area."),
    body: text(2000).min(1, "Write the post."),
    mediaId: z.uuid().nullable(),
    /** Null posts it now. */
    scheduledFor: z.iso.datetime({ offset: true }).nullable().default(null),
  })
  .strict();

export interface ReviewRow {
  id: string;
  author: string;
  stars: number;
  text: string;
  postedAt: string;
  clientId: string | null;
  reply: string | null;
  replyStatus: "none" | "draft" | "approved" | "posted";
  suggested: string;
}
export interface GbpPostRow {
  id: string;
  service: string;
  area: string;
  body: string;
  mediaId: string | null;
  status: "draft" | "scheduled" | "held" | "posted";
  scheduledFor: string | null;
  postedAt: string | null;
  createdAt: string;
}
export interface GbpAnswer {
  profile: {
    id: string;
    placeId: string;
    name: string;
    /** The listing's address on Google, so the owner can see it is the right business. "" until the next read. */
    address: string;
    /** The listing on Google Maps. */
    mapsUrl: string;
    description: string;
    services: string[];
    rating: number | null;
    reviewCount: number;
    syncedAt: string | null;
    problems: string[];
  } | null;
  reviews: ReviewRow[];
  posts: GbpPostRow[];
  waitingReplies: number;
  /** The Google profile or a Facebook Page is linked, so approved posts and replies can go out. */
  postingConnected: boolean;
  /** The Google account saved to manage the profile (Marketing settings), or null when none is saved. */
  managedBy: string | null;
}

/** A Google listing the owner can pick as the company's own. */
export interface GbpChoice {
  placeId: string;
  name: string;
  address: string;
  reviews: number;
  mapsUrl: string;
}

// Sign in with Google or Facebook (2026-11-01) --------------------------------------------------------------------------

/** Google (ad accounts, the business profile, Search Console) or Meta (ad accounts, the Facebook Page and its Instagram). */
export const SIGNIN_KINDS = ["google", "meta"] as const;
export type SigninKind = (typeof SIGNIN_KINDS)[number];
export const signinKind = z.enum(SIGNIN_KINDS);
/** Where to come back to after signing in: the web app's own address the person is on. */
export const signinStartBody = z.object({ kind: signinKind, back: z.url().max(200) }).strict();

export interface SigninOne {
  /** The Google email or the Facebook name that signed in. */
  who: string;
  at: string;
  /** When the sign in runs out (Meta's last 60 days), or null. */
  until: string | null;
  /** Signed in on this company, or passed down from the brand above it. */
  own: boolean;
  /** Google or Facebook took the sign in back (Testing apps do it after 7 days, or someone removed access): sign in again. */
  signedOut?: boolean;
}

export interface SigninAnswer {
  /** Whether head office saved the Google sign-in client and the Meta app. */
  ready: Record<SigninKind, boolean>;
  google: SigninOne | null;
  meta: SigninOne | null;
}

/** What the signed in accounts can reach, to pick from. A side that could not be read says why in `error`. */
export interface SigninChoices {
  google: {
    ads: { id: string; name: string; managerId: string }[];
    /** location: Google's own name (accounts/../locations/..). onHere: the profile's row on this company, when it is used here. */
    profiles: { placeId: string; name: string; address: string; location: string; onHere: string | null }[];
    error: string | null;
  } | null;
  meta: {
    adAccounts: { id: string; name: string }[];
    pages: { id: string; name: string; instagram: boolean }[];
    error: string | null;
  } | null;
}

/** A listing on Google Maps by its place id. */
export const placeMapsUrl = (placeId: string) => `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`;

// Ranks ----------------------------------------------------------------------------------------------------------------

const gridSize = z
  .number()
  .int()
  .refine((n) => (GRID_SIZES as readonly number[]).includes(n), "Pick 3, 5, 7, 9, 11 or 13 points a side.");

/** One listing's circle: which Google listing (null for the company's own), its middle, spots across, miles out, spots off. */
export const rankCircle = z
  .object({
    listing: z.uuid().nullable(),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    size: gridSize,
    radius: z.number().min(1).max(50),
    skip: z.array(z.number().int().min(0).max(168)).max(169),
  })
  .strict();
export type RankCircle = z.infer<typeof rankCircle>;

export const rankGridBody = z
  .object({
    keyword: text(60).min(1, "Type a keyword."),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    size: z
      .number()
      .int()
      .refine((n) => (GRID_SIZES as readonly number[]).includes(n), "Pick 3, 5, 7, 9, 11 or 13 points a side."),
    /** Miles between spots, for a square with no area. With an area the spacing follows it. */
    miles: z.number().min(0.25).max(10).default(1),
    /** The service area in miles from the middle; 0 checks the whole square. */
    radius: z.number().min(0).max(50).default(0),
    /** Spots turned off (water, no homes), by their place in the square. */
    skip: z.array(z.number().int().min(0).max(168)).max(169).default([]),
    competitors: z.array(text(120).min(1)).max(5).default([]),
    /** A circle for each of our Google listings; with these, lat to skip above are the first one's. */
    areas: z.array(rankCircle).max(3).optional(),
  })
  .strict();

/** The circles for every tracked keyword, one for each of our Google listings checked. */
export const rankAreaBody = z.object({ areas: z.array(rankCircle).min(1, "Pick at least one listing.").max(3) }).strict();

/** One run of a grid: the spots checked (lat, lng), our place at each (null past 20) and the top 3 there. */
export interface RankRun {
  runOn: string;
  average: number;
  top3: number;
  found: number;
  spots: [number, number][];
  points: (number | null)[];
  /** The first 3 businesses at each spot, best first. Empty for runs from before they were kept. */
  tops: string[][];
  /** The listings checked, in circle order; one (the company) for runs from before listings were kept apart. */
  who: { id: string | null; name: string }[];
  /** Each listing's place at each spot (null past 20); empty for older runs. */
  each: (number | null)[][];
  /** Each listing's circle at the time of the run. */
  circles: { lat: number; lng: number; radius: number }[];
}

export interface RankRival {
  name: string;
  /** Points where they came in above us. */
  ahead: number;
  best: number;
  average: number;
  rating: number | null;
  reviews: number | null;
}

export interface RankGridRow {
  id: string;
  keyword: string;
  lat: number;
  lng: number;
  size: number;
  miles: number;
  radius: number;
  skip: number[];
  /** A circle for each listing checked (one, the company's own, until more are set). */
  areas: RankCircle[];
  competitors: string[];
  lastRunAt: string | null;
  latest: {
    runOn: string;
    points: (number | null)[];
    average: number;
    top3: number;
    found: number;
    competitors: { name: string; average: number }[];
    /** The businesses ahead of us most in this run, with Google's rating and review count (null when not looked up). */
    rivals: RankRival[];
    /** Our own rating and review count at the time of the run, or null when Google had none for us. */
    us: { rating: number | null; reviews: number | null } | null;
  } | null;
  /** Averages of the last runs, oldest first, for the trend. */
  trend: { runOn: string; average: number }[];
  /** The last 12 runs with their spots, newest first, to show any of them on the map. */
  runs: RankRun[];
}

export interface RankGridsAnswer {
  grids: RankGridRow[];
  /** Our Google listing's spot, to center a new area on and pin on the map. Null with no listing or no spot found. */
  home: { lat: number; lng: number; name: string } | null;
  /** Every Google listing of ours, the main one first, with its map spot when Google has one. */
  listings: { id: string; name: string; lat: number | null; lng: number | null }[];
  /** The searches this trade starts with. */
  suggested: string[];
}

export const webKeywordsBody = z
  .object({ keywords: z.array(text(80).min(1)).max(WEB_RANK_MAX, `Track ${String(WEB_RANK_MAX)} keywords at most.`) })
  .strict();

export interface WebRankRow {
  keyword: string;
  kind: "tracked" | "top";
  position: number | null;
  prior: number | null;
  change: number | null;
  clicks: number;
  impressions: number;
  ctr: number | null;
  page: string | null;
}
export interface WebRankAnswer {
  /** False while Search Console is not connected. */
  connected: boolean;
  site: string | null;
  checkedAt: string | null;
  windows: { current: { from: string; to: string }; prior: { from: string; to: string } };
  tracked: WebRankRow[];
  top: WebRankRow[];
}

export const pageBriefBody = z
  .object({ url: text(500).min(1, "Type the page address."), keyword: text(80).min(1, "Type a keyword.") })
  .strict();

export interface PageBriefRow {
  id: string;
  url: string;
  keyword: string;
  brief: PageBrief;
  score: number;
  parts: { name: string; got: number; tip: string | null }[];
  checkedAt: string;
}

// Ads, conversions and channels ----------------------------------------------------------------------------------------

export interface ChannelRow {
  channel: Channel;
  leads: number;
  newClients: number;
  revenueCents: number;
  spendCents: number;
  netCents: number;
  timesBack: number | null;
  costPerClientCents: number | null;
}
export interface AdsAnswer {
  from: string;
  to: string;
  spend: { platform: "gads" | "lsa" | "facebook"; spendCents: number; clicks: number; leads: number }[];
  days: { day: string; gads: number; lsa: number; facebook: number }[];
  channels: ChannelRow[];
  conversions: {
    id: string;
    platform: "meta" | "google";
    event: "booked" | "paid";
    valueCents: number;
    status: "queued" | "sent" | "held" | "failed";
    eventAt: string;
  }[];
  conversionsLive: boolean;
  /** False while no ad account is connected, so no spend is read. */
  spendConnected: boolean;
}

// Social and media -----------------------------------------------------------------------------------------------------

export const mediaAddBody = z
  .object({ fileIds: z.array(z.uuid()).min(1).max(50), tags: z.array(text(30).min(1)).max(10).default([]) })
  .strict();
export const mediaPatchBody = z
  .object({ caption: text(140).optional(), tags: z.array(text(30).min(1)).max(10).optional() })
  .strict();
/** A photo added from a phone or a computer (posts.php "Add a photo", media.php). */
export const mediaUploadBody = z
  .object({
    name: text(200).min(1),
    mime: z.enum(["image/jpeg", "image/png", "image/webp"], "Use a JPEG, PNG or WebP photo."),
    dataBase64: z.string().max(14_000_000, "That photo is too big. The most is 10 MB."),
    caption: text(140).default(""),
  })
  .strict();
export const SOCIAL_PLATFORMS = ["facebook", "instagram", "google"] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];
/** A new or changed post. A draft may have a day or not; a scheduled post needs one. */
export const socialPostBody = z
  .object({
    body: text(2400).min(1, "Write the post."),
    mediaIds: z.array(z.uuid()).max(10),
    platforms: z.array(z.enum(SOCIAL_PLATFORMS)).min(1, "Pick where it goes: Facebook, Instagram or Google."),
    scheduledFor: z.iso.datetime({ offset: true }).nullable(),
    draft: z.boolean().default(false),
  })
  .strict();
/** Schedules a month's drafts that are ready. */
export const socialMonthBody = z.object({ month: z.string().regex(/^\d{4}-\d{2}$/) }).strict();

export interface MediaRow {
  id: string;
  fileId: string;
  url: string;
  caption: string;
  tags: string[];
  source: "visit" | "upload";
  blocked: boolean;
  /** How many posts use it. */
  used: number;
  createdAt: string;
}
export interface VisitPhotoPick {
  fileId: string;
  url: string;
  client: string;
  date: string;
  kind: string;
  inLibrary: boolean;
}
/** How a post went at one place. */
export interface SocialResult {
  ok: boolean;
  at: string;
  err: string | null;
}
export interface SocialPostRow {
  id: string;
  body: string;
  mediaIds: string[];
  platforms: SocialPlatform[];
  /** held: it came due but did not all go out (posting off or not linked, or a place said no). */
  status: "draft" | "scheduled" | "held" | "posted";
  scheduledFor: string | null;
  postedAt: string | null;
  results: Partial<Record<SocialPlatform, SocialResult>>;
  createdAt: string;
}

// Cold email -----------------------------------------------------------------------------------------------------------

export const sequenceBody = z
  .object({
    name: text(80).min(1, "Name the sequence."),
    fromName: text(80).min(1, "Type the sender's name."),
    fromEmail: z.string().trim().toLowerCase().pipe(z.email("Type a full email address.")),
    mailingAddress: text(200).min(5, "Type the mailing address. Every cold email must show one."),
    steps: z
      .array(
        z.object({
          days: z.number().int().min(0).max(60),
          subject: text(150),
          body: text(4000).min(1, "Write each email."),
        }),
      )
      .min(1)
      .max(OUTREACH_MAX_STEPS),
    dailyCap: z.number().int().min(1).max(OUTREACH_MAX_CAP),
    ramp: z.boolean(),
  })
  .strict();
export const sendingBody = z.object({ on: z.boolean() }).strict();
export const prospectBody = z
  .object({
    business: text(120).min(1, "Type the business name."),
    email: z.string().trim().toLowerCase().pipe(z.email("Type a full email address.")),
    firstName: text(40).nullable().default(null),
    website: text(300).nullable().default(null),
    phone: text(20).nullable().default(null),
    address: text(200).nullable().default(null),
    zip: z
      .string()
      .regex(/^\d{5}$/)
      .nullable()
      .default(null),
    category: text(80).nullable().default(null),
  })
  .strict();
export const enrollBody = z.object({ sequenceId: z.uuid(), prospectIds: z.array(z.uuid()).min(1).max(500) }).strict();
export const aiLinesBody = z.object({ prospectIds: z.array(z.uuid()).min(1).max(10) }).strict();
/** A reply the outreach inbox got (read from the inbox by the reply check; typed in by hand in this build). */
export const outreachReplyBody = z
  .object({ from: text(200).min(3), subject: text(300), body: text(5000), auto: z.boolean().default(false) })
  .strict();

export interface SequenceRow {
  id: string;
  name: string;
  fromName: string;
  fromEmail: string;
  mailingAddress: string;
  steps: { days: number; subject: string; body: string }[];
  dailyCap: number;
  ramp: boolean;
  sending: boolean;
  todayCap: number;
  sentToday: number;
  prospects: { active: number; replied: number; done: number; stopped: number };
}
export interface ProspectRow {
  id: string;
  business: string;
  firstName: string | null;
  email: string;
  website: string | null;
  category: string | null;
  zip: string | null;
  sequenceId: string | null;
  step: number;
  nextAt: string | null;
  status: "new" | "active" | "replied" | "done" | "unsub" | "bounced" | "paused";
  aiLine: string | null;
  note: string | null;
  leadId: string | null;
}
export interface OutreachAnswer {
  sequences: SequenceRow[];
  prospects: ProspectRow[];
  sent: { id: string; business: string; step: number; subject: string; status: string; createdAt: string }[];
  aiLinesAllowed: boolean;
  finderAllowed: boolean;
}

// Lead finder ----------------------------------------------------------------------------------------------------------

export const finderBody = z
  .object({
    category: text(80).min(2, "Type what kind of business."),
    area: text(80).min(2, "Type a city or area."),
    big: z.boolean().default(false),
  })
  .strict();

export interface FinderRow {
  id: string;
  category: string;
  area: string;
  big: boolean;
  target: number;
  pages: number;
  pagesPaid: number;
  found: number;
  added: number;
  skipped: number;
  /** Big search: businesses from pages not paid for, waiting for a free single lookup (owner answer 122). */
  spare: number;
  status: "running" | "done" | "stopped";
  note: string | null;
  createdAt: string;
}

export const adSpendQuery = z.object({ from: day.optional(), to: day.optional() });
